import { useEffect, useState, useCallback } from 'react';
import API from '../../api';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import SearchSelect from '../../components/SearchSelect';

const STATUT_LABELS = { en_attente: 'En attente', en_cours: 'En cours', termine: 'Terminé' };
const STATUT_CLASSES = { en_attente: 'badge-warning', en_cours: 'badge-success', termine: 'badge-muted' };
const TYPE_ICONS = { pdf: 'bi-file-earmark-pdf', audio: 'bi-file-earmark-music', video: 'bi-file-earmark-play', autre: 'bi-file-earmark' };

export default function EnseignantCours() {
  const [cours, setCours] = useState([]);
  const [facultes, setFacultes] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [universites, setUniversites] = useState([]);
  const [form, setForm] = useState({ titre: '', description: '', faculte: '', promotion: '', universite: '', date_debut: '', date_fin: '' });
  const [editId, setEditId] = useState(null);
  const [statutFilter, setStatutFilter] = useState('');
  const [expandedCours, setExpandedCours] = useState({});
  const { addToast } = useToast();
  const confirm = useConfirm();

  const load = useCallback(() => {
    const url = statutFilter ? `cours/?statut=${statutFilter}` : 'cours/';
    API.get(url).then((r) => setCours(r.data.results || r.data));
  }, [statutFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    API.get('facultes/').then((r) => setFacultes(r.data.results || r.data));
    API.get('promotions/').then((r) => setPromotions(r.data.results || r.data));
    API.get('universites/').then((r) => setUniversites(r.data.results || r.data));
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    try {
      if (editId) { await API.put(`cours/${editId}/`, payload); addToast('Cours modifié', 'success'); }
      else { await API.post('cours/', payload); addToast('Cours ajouté', 'success'); }
      resetForm();
      load();
    } catch { addToast("Erreur lors de l'opération", 'error'); }
  };

  const resetForm = () => {
    setEditId(null);
    setForm({ titre: '', description: '', faculte: '', promotion: '', universite: '', date_debut: '', date_fin: '' });
  };

  const handleEdit = (c) => {
    setEditId(c.id);
    setForm({
      titre: c.titre, description: c.description || '',
      faculte: c.faculte, promotion: c.promotion,
      universite: c.universite || '',
      date_debut: c.date_debut || '', date_fin: c.date_fin || '',
    });
  };

  const handleDelete = async (id) => {
    if (await confirm('Supprimer ce cours et toutes ses données ?')) {
      await API.delete(`cours/${id}/`);
      addToast('Cours supprimé', 'info');
      load();
    }
  };

  // ── Supports (fichiers) ──
  const toggleSection = (coursId, section) => {
    setExpandedCours((prev) => {
      const key = `${coursId}_${section}`;
      const cur = { ...prev };
      if (cur[key]) { delete cur[key]; return cur; }
      cur[key] = true;
      // Charger les données si pas encore fait
      if (section === 'supports') loadSupports(coursId);
      if (section === 'participants') loadParticipants(coursId);
      return cur;
    });
  };

  const [supportsMap, setSupportsMap] = useState({});
  const [participantsMap, setParticipantsMap] = useState({});
  const [uploadForm, setUploadForm] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  const ALLOWED_EXTS = { '.pdf': 'pdf', '.mp3': 'audio', '.mp4': 'video' };
  const MAX_SIZE = 100 * 1024 * 1024; // 100 MB

  const loadSupports = (coursId) => {
    API.get(`supports/?cours=${coursId}`).then((r) =>
      setSupportsMap((m) => ({ ...m, [coursId]: r.data.results || r.data }))
    );
  };
  const loadParticipants = (coursId) => {
    API.get(`cours/${coursId}/participants/`).then((r) =>
      setParticipantsMap((m) => ({ ...m, [coursId]: r.data }))
    );
  };

  const handleUpload = async (coursId) => {
    const uf = uploadForm[coursId];
    if (!uf || !uf.fichier) { addToast('Sélectionnez un fichier', 'error'); return; }

    const file = uf.fichier;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTS[ext]) {
      addToast(`Format non autorisé (${ext}). Acceptés : .pdf, .mp3, .mp4`, 'error');
      return;
    }
    if (file.size > MAX_SIZE) {
      addToast(`Fichier trop volumineux (${(file.size / (1024 * 1024)).toFixed(1)} Mo). Max : 100 Mo`, 'error');
      return;
    }

    const fd = new FormData();
    fd.append('cours', coursId);
    fd.append('titre', uf.titre || file.name.replace(/\.[^.]+$/, ''));
    fd.append('type_support', ALLOWED_EXTS[ext] || 'autre');
    fd.append('fichier', file);
    try {
      setUploadProgress((p) => ({ ...p, [coursId]: 0 }));
      await API.post('supports/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / (e.total || 1));
          setUploadProgress((p) => ({ ...p, [coursId]: pct }));
        },
      });
      addToast('Fichier ajouté', 'success');
      setUploadForm((p) => ({ ...p, [coursId]: {} }));
      loadSupports(coursId);
    } catch (err) {
      const detail = err.response?.data?.fichier?.[0] || err.response?.data?.detail || "Erreur lors de l'upload";
      addToast(detail, 'error');
    } finally {
      setUploadProgress((p) => { const n = { ...p }; delete n[coursId]; return n; });
    }
  };

  const deleteSupport = async (coursId, supportId) => {
    if (await confirm('Supprimer ce fichier ?')) {
      await API.delete(`supports/${supportId}/`);
      addToast('Fichier supprimé', 'info');
      loadSupports(coursId);
    }
  };

  const downloadSupport = (supportId, titre, typeSupport) => {
    API.get(`supports/${supportId}/download/`, { responseType: 'blob' })
      .then((res) => {
        const contentDisp = res.headers['content-disposition'] || '';
        const match = contentDisp.match(/filename="?([^"]+)"?/);
        let filename = match ? match[1] : null;
        if (!filename) {
          const extMap = { pdf: '.pdf', audio: '.mp3', video: '.mp4' };
          const ext = extMap[typeSupport] || '';
          filename = (titre || 'support') + ext;
        }
        const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => addToast('Erreur téléchargement', 'error'));
  };

  // ── Participants ──
  const retirerParticipant = async (coursId, etudiantId) => {
    if (await confirm('Retirer cet étudiant du cours ?')) {
      await API.post(`cours/${coursId}/retirer-participant/`, { etudiant_id: etudiantId });
      addToast('Étudiant retiré', 'info');
      loadParticipants(coursId);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2><i className="bi bi-book" /> Mes cours</h2>
      </div>

      {/* ── Formulaire ── */}
      <div className="form-glass">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Titre du cours</label>
              <input className="form-control" name="titre" placeholder="Titre" value={form.titre} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Université</label>
              <SearchSelect name="universite" value={form.universite} onChange={handleChange}
                options={universites.map((u) => ({ value: u.id, label: u.sigle ? `${u.sigle} - ${u.nom}` : u.nom }))}
                placeholder="-- Sélectionner --" required />
            </div>
            <div className="form-group">
              <label>Faculté</label>
              <SearchSelect name="faculte" value={form.faculte} onChange={handleChange}
                options={facultes.map((f) => ({ value: f.id, label: f.nom }))}
                placeholder="-- Sélectionner --" required />
            </div>
            <div className="form-group">
              <label>Promotion</label>
              <SearchSelect name="promotion" value={form.promotion} onChange={handleChange}
                options={promotions.map((p) => ({ value: p.id, label: p.nom }))}
                placeholder="-- Sélectionner --" required />
            </div>
            <div className="form-group">
              <label>Date de début</label>
              <input type="date" className="form-control" name="date_debut" value={form.date_debut} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Date fin</label>
              <input type="date" className="form-control" name="date_fin" value={form.date_fin} onChange={handleChange} required />
            </div>
            <div className="form-group full-width">
              <label>Description</label>
              <textarea className="form-control" name="description" placeholder="Description du cours" value={form.description} onChange={handleChange} />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              <i className={editId ? 'bi bi-check-lg' : 'bi bi-plus-lg'} /> {editId ? 'Modifier' : 'Ajouter'}
            </button>
            {editId && (
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                <i className="bi bi-x-lg" /> Annuler
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ── Filtre par statut ── */}
      <div className="filter-bar">
        <select className="select-filter" value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)}>
          <option value="">Tous les statuts</option>
          <option value="en_attente">En attente</option>
          <option value="en_cours">En cours</option>
          <option value="termine">Terminé</option>
        </select>
      </div>

      {/* ── Liste des cours ── */}
      {cours.length === 0 ? (
        <div className="empty-state"><i className="bi bi-book" /><p>Aucun cours trouvé</p></div>
      ) : (
        <div className="cards-grid">
          {cours.map((c) => (
            <div key={c.id} className="course-card">
              <div className="course-card-header">
                <h3>{c.titre}</h3>
                <span className={`badge-status ${STATUT_CLASSES[c.statut] || ''}`}>{STATUT_LABELS[c.statut] || c.statut}</span>
              </div>
              <p>{c.description}</p>
              <div className="card-meta">
                {c.faculte_nom && <span><i className="bi bi-building" /> {c.faculte_nom}</span>}
                {c.promotion_label && <span><i className="bi bi-mortarboard" /> {c.promotion_label}</span>}
                {c.annee_academique && <span><i className="bi bi-calendar-range" /> {c.annee_academique}</span>}
                {c.date_debut && <span><i className="bi bi-calendar-event" /> {c.date_debut} → {c.date_fin || '...'}</span>}
                <span><i className="bi bi-people" /> {c.nb_participants} participant{c.nb_participants !== 1 ? 's' : ''}</span>
              </div>

              <div className="card-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => toggleSection(c.id, 'supports')}>
                  <i className={expandedCours[`${c.id}_supports`] ? 'bi bi-eye-slash' : 'bi bi-folder2-open'} /> Fichiers
                </button>
                <button className="btn btn-sm btn-secondary" onClick={() => toggleSection(c.id, 'participants')}>
                  <i className={expandedCours[`${c.id}_participants`] ? 'bi bi-eye-slash' : 'bi bi-people'} /> Participants
                </button>
                <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(c)}><i className="bi bi-pencil" /> Modifier</button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id)}><i className="bi bi-trash" /> Supprimer</button>
              </div>

              {/* ── Fichiers / Supports ── */}
              {expandedCours[`${c.id}_supports`] && (
                <div className="section-expand">
                  <h4><i className="bi bi-paperclip" /> Fichiers du cours</h4>
                  <div className="upload-row">
                    <input type="text" className="form-control" placeholder="Titre (optionnel)"
                      value={uploadForm[c.id]?.titre || ''} onChange={(e) => setUploadForm((p) => ({ ...p, [c.id]: { ...p[c.id], titre: e.target.value } }))} />
                    <input type="file" className="form-control"
                      accept=".pdf,.mp3,.mp4"
                      onChange={(e) => setUploadForm((p) => ({ ...p, [c.id]: { ...p[c.id], fichier: e.target.files[0] } }))} />
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => handleUpload(c.id)}
                      disabled={uploadProgress[c.id] !== undefined}>
                      <i className="bi bi-upload" /> {uploadProgress[c.id] !== undefined ? `${uploadProgress[c.id]}%` : 'Envoyer'}
                    </button>
                  </div>
                  {uploadProgress[c.id] !== undefined && (
                    <div className="upload-progress">
                      <div className="upload-progress-bar" style={{ width: `${uploadProgress[c.id]}%` }} />
                    </div>
                  )}
                  <p className="text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>Formats acceptés : PDF, MP3, MP4 — Max 100 Mo</p>
                  {(supportsMap[c.id] || []).length === 0 ? (
                    <p className="text-muted">Aucun fichier pour ce cours.</p>
                  ) : (
                    <div className="supports-list">
                      {supportsMap[c.id].map((s) => (
                        <div key={s.id} className="support-item">
                          <span>
                            <i className={`bi ${TYPE_ICONS[s.type_support] || 'bi-file-earmark'}`} /> {s.titre}
                            <span className="badge-type">{s.type_support}</span>
                            {s.taille_display && <span className="support-meta">{s.taille_display}</span>}
                          </span>
                          <div className="support-actions">
                            <button className="btn btn-sm btn-primary" onClick={() => downloadSupport(s.id, s.titre, s.type_support)} title="Télécharger"><i className="bi bi-download" /></button>
                            <button className="btn btn-sm btn-danger" onClick={() => deleteSupport(c.id, s.id)} title="Supprimer"><i className="bi bi-trash" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Participants ── */}
              {expandedCours[`${c.id}_participants`] && (
                <div className="section-expand">
                  <h4><i className="bi bi-people" /> Participants inscrits</h4>
                  {(participantsMap[c.id] || []).length === 0 ? (
                    <p className="text-muted">Aucun participant inscrit.</p>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr><th>Nom</th><th>Username</th><th>Email</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {participantsMap[c.id].map((p) => (
                          <tr key={p.id}>
                            <td>{p.etudiant_nom}</td>
                            <td>{p.username}</td>
                            <td>{p.email}</td>
                            <td>
                              <button className="btn btn-sm btn-danger" onClick={() => retirerParticipant(c.id, p.etudiant)}>
                                <i className="bi bi-person-dash" /> Retirer
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
